/**
 * Самостоятельная регистрация компании на главной странице (ГЛ-05, МР-09): название, имя, почта, страна,
 * согласие с офертой и политикой данных. После отправки приходит письмо со ссылкой подтверждения.
 * Метки перехода (канал, кампания, партнёр) уходят вместе с формой.
 */
import type { PublicCountry } from '@epc/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { type FormEvent, useEffect, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { captureAttribution } from '../../lib/attribution';
import { ErrorBox } from '../../ui/feedback';
import { Checkbox, Field, Input, Select } from '../../ui/form';
import type { Dictionary, Lang } from './i18n';

interface PublicLegalDoc {
  id: string;
  kind: string;
  lang: string;
  version: string;
  title: string;
  body: string;
}

export function SignupForm({ lang, t }: { lang: Lang; t: Dictionary['signup'] }) {
  const countries = useQuery({
    queryKey: ['public', 'countries'],
    queryFn: () => api<PublicCountry[]>('/public/countries'),
    staleTime: 300_000,
  });
  const [country, setCountry] = useState('');
  const [f, setF] = useState({ company: '', name: '', email: '', website: '' });
  const [accept, setAccept] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sent, setSent] = useState(false);

  // Страна: из ссылки, иначе Казахстан, иначе первая из списка
  useEffect(() => {
    if (country || !countries.data?.length) return;
    const wanted = captureAttribution().countryCode;
    const list = countries.data;
    setCountry(
      list.find((c) => c.code === wanted)?.code ?? list.find((c) => c.code === 'KZ')?.code ?? list[0]!.code,
    );
  }, [country, countries.data]);

  const legal = useQuery({
    queryKey: ['public', 'legal', country, lang],
    queryFn: () => api<PublicLegalDoc[]>('/public/legal', { query: { country, lang } }),
    enabled: Boolean(country),
    staleTime: 300_000,
  });
  const docs = (legal.data ?? []).filter((d) => d.kind === 'offer' || d.kind === 'privacy');
  const hasOffer = docs.some((d) => d.kind === 'offer');

  const send = useMutation({
    mutationFn: () =>
      api('/public/signup', {
        body: {
          company: f.company,
          name: f.name,
          email: f.email,
          countryCode: country,
          lang,
          acceptTerms: accept,
          website: f.website,
          ...(({ channel, campaign, partner }) => ({ channel, campaign, partner }))(captureAttribution()),
        },
      }),
    onSuccess: () => setSent(true),
    onError: (err) => err instanceof ApiError && setErrors(err.fieldErrors()),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    send.mutate();
  }
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((x) => ({ ...x, [k]: e.target.value }));

  if (sent) {
    return (
      <div className="rounded-lg border border-line bg-surface p-6" role="status">
        <h3 className="text-[20px] leading-7 font-semibold text-ink">{t.sentTitle}</h3>
        <p className="mt-2 text-[14px] leading-6 text-ink-2">{t.sentText}</p>
      </div>
    );
  }

  const ready = f.company.trim() && f.name.trim() && f.email.trim() && country && accept && hasOffer;
  return (
    <form
      onSubmit={submit}
      noValidate
      className="grid gap-4 rounded-lg border border-line bg-surface p-5 sm:grid-cols-2 md:p-6"
    >
      <Field label={t.company} htmlFor="su-company" error={errors.company} className="sm:col-span-2">
        <Input
          id="su-company"
          className="h-11!"
          value={f.company}
          onChange={set('company')}
          autoComplete="organization"
          invalid={!!errors.company}
          required
        />
      </Field>
      <Field label={t.name} htmlFor="su-name" error={errors.name}>
        <Input
          id="su-name"
          className="h-11!"
          value={f.name}
          onChange={set('name')}
          autoComplete="name"
          invalid={!!errors.name}
          required
        />
      </Field>
      <Field label={t.email} htmlFor="su-email" error={errors.email}>
        <Input
          id="su-email"
          className="h-11!"
          type="email"
          inputMode="email"
          value={f.email}
          onChange={set('email')}
          autoComplete="email"
          invalid={!!errors.email}
          required
        />
      </Field>
      <Field label={t.country} htmlFor="su-country" error={errors.countryCode} className="sm:col-span-2">
        <Select
          id="su-country"
          className="h-11!"
          value={country}
          onChange={(e) => {
            setCountry(e.target.value);
            setAccept(false);
          }}
        >
          {(countries.data ?? []).map((c) => (
            <option key={c.code} value={c.code}>
              {lang === 'en' ? c.nameEn : c.name}
            </option>
          ))}
        </Select>
      </Field>
      {/* Поле-ловушка для роботов: людям не видно */}
      <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden>
        <label htmlFor="su-website">Website</label>
        <input id="su-website" tabIndex={-1} autoComplete="off" value={f.website} onChange={set('website')} />
      </div>
      <div className="flex flex-col gap-2 sm:col-span-2">
        {legal.isPending && country ? (
          <p className="text-[13px] text-ink-2">{t.docsLoading}</p>
        ) : docs.length ? (
          <>
            <ul className="flex flex-col">
              {docs.map((d) => (
                <li key={d.id}>
                  <details className="group">
                    <summary className="flex min-h-11 cursor-pointer items-center text-[14px] font-medium text-ink underline-offset-4 hover:underline">
                      {d.title} ({d.version})
                    </summary>
                    <div className="mb-2 max-h-56 overflow-y-auto rounded-md border border-line bg-surface-2 p-3 text-[13px] leading-5 whitespace-pre-wrap text-ink-2 [overflow-wrap:anywhere]">
                      {d.body}
                    </div>
                  </details>
                </li>
              ))}
            </ul>
            <Checkbox
              label={`${t.accept} ${t.offer}`}
              checked={accept}
              onChange={(e) => setAccept(e.target.checked)}
              className={clsx('min-h-11 items-center', errors.acceptTerms && 'text-danger')}
            />
            {errors.acceptTerms ? (
              <p className="text-[12px] text-danger" role="alert">
                {errors.acceptTerms}
              </p>
            ) : null}
          </>
        ) : legal.isSuccess || legal.isError ? (
          <p className="text-[13px] text-ink-2">{t.docsMissing}</p>
        ) : null}
      </div>
      <ErrorBox className="sm:col-span-2" error={Object.keys(errors).length ? null : send.error} />
      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={!ready || send.isPending}
          className="inline-flex h-12 w-full items-center justify-center rounded-md bg-accent px-5 text-[15px] font-semibold text-on-accent underline-offset-[3px] hover:underline disabled:cursor-not-allowed disabled:bg-tile disabled:text-ink-2 disabled:no-underline sm:w-auto"
        >
          {send.isPending ? t.sending : t.submit}
        </button>
      </div>
    </form>
  );
}
